﻿/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Cog6ToothIcon,
  BellIcon,
  ClockIcon,
  CloudIcon,
  MapPinIcon,
  PresentationChartLineIcon,
  PhotoIcon,
  MagnifyingGlassPlusIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  LanguageIcon,
  ArrowRightStartOnRectangleIcon as LogoutIcon,
} from "@heroicons/react/24/outline";
import { NotificationSettings } from "./settings/NotificationSettings";
import { TimeFormatSettings } from "./settings/TimeFormatSettings";
import { DistanceSettings } from "./settings/DistanceSettings";
import { DashboardSettings } from "./settings/DashboardSettings";
import { WeatherSettings } from "./settings/WeatherSettings";
import { WallpaperSettings } from "./settings/WallpaperSettings";
import { InterfaceSettings } from "./settings/InterfaceSettings";
import { KioskSettings } from "./settings/KioskSettings";
import { SecuritySettings } from "./settings/SecuritySettings";
import { LanguageSettings } from "./settings/LanguageSettings";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/utils/language";
import { useAuth } from "@/context/auth";

interface SettingsSection {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ComponentType;
  group: "general" | "appearance" | "system";
}

export const iconColors: Record<string, string> = {
  language: "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
  notifications: "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
  "time-format": "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
  distance: "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
  dashboard: "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
  weather: "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
  wallpaper: "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
  interface: "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
  kiosk: "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
  security: "bg-gray-200/70 text-gray-600 dark:bg-white/[0.07] dark:text-gray-300",
};

const settingsSections: SettingsSection[] = [
  { id: "language",      label: "Language",        icon: LanguageIcon,              component: LanguageSettings,     group: "general"    },
  { id: "notifications", label: "Notifications",   icon: BellIcon,                  component: NotificationSettings, group: "general"    },
  { id: "time-format",   label: "Time & Timezone", icon: ClockIcon,                 component: TimeFormatSettings,   group: "general"    },
  { id: "distance",      label: "Distance Units",  icon: MapPinIcon,                component: DistanceSettings,     group: "general"    },
  { id: "dashboard",     label: "Dashboard",       icon: PresentationChartLineIcon, component: DashboardSettings,    group: "appearance" },
  { id: "weather",       label: "Weather",         icon: CloudIcon,                 component: WeatherSettings,      group: "appearance" },
  { id: "wallpaper",     label: "Wallpaper",       icon: PhotoIcon,                 component: WallpaperSettings,    group: "appearance" },
  { id: "interface",     label: "Interface scale", icon: MagnifyingGlassPlusIcon,   component: InterfaceSettings,    group: "appearance" },
  { id: "kiosk",         label: "Kiosk",           icon: LockClosedIcon,            component: KioskSettings,        group: "system"     },
  { id: "security",      label: "Security",        icon: ShieldCheckIcon,           component: SecuritySettings,     group: "system"     },
];

const groups = [
  { id: "general",    label: "General",    labelRu: "Основные"     },
  { id: "appearance", label: "Appearance", labelRu: "Внешний вид"  },
  { id: "system",     label: "System",     labelRu: "Система"      },
] as const;

const ruLabels: Record<string, string> = {
  language: "Язык", notifications: "Уведомления", "time-format": "Время и часовой пояс",
  distance: "Единицы расстояния", dashboard: "Дашборд", weather: "Погода",
  wallpaper: "Обои", kiosk: "Киоск", security: "Безопасность",
};

interface SettingsMenuProps {
  triggerLabel?: string;
  triggerClassName?: string;
  /** Text shown next to the icon without taking over the global open-settings event. */
  inlineLabel?: React.ReactNode;
  onOpenSection?: () => void;
}

export const SettingsMenu: React.FC<SettingsMenuProps> = ({ triggerLabel, triggerClassName, inlineLabel, onOpenSection }) => {
  const { language, t } = useLanguage();
  const { logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(settingsSections[0].id);

  const isRu = language === "ru";
  const labelFor = (s: SettingsSection) => isRu ? (ruLabels[s.id] || s.label) : s.label;

  const openModal = () => {
    setShowModal(true);
    onOpenSection?.();
  };

  useEffect(() => {
    if (triggerLabel) return;
    const open = (e: Event) => {
      const section = (e as CustomEvent<{ section?: string }>).detail?.section;
      if (section && settingsSections.some((s) => s.id === section)) {
        setActiveSection(section);
      }
      setShowModal(true);
    };
    window.addEventListener("nyrvex:open-settings", open);
    return () => window.removeEventListener("nyrvex:open-settings", open);
  }, [triggerLabel]);

  const activeData = settingsSections.find((s) => s.id === activeSection);
  const ActiveComponent = activeData?.component ?? NotificationSettings;
  const ActiveIcon = activeData?.icon;

  return (
    <>
      {inlineLabel ? (
        <button
          type="button"
          className={triggerClassName}
          aria-label="Settings"
          aria-haspopup="dialog"
          onClick={openModal}
        >
          <Cog6ToothIcon className="h-[19px] w-[19px] flex-none" />
          {inlineLabel}
        </button>
      ) : (
        <Button
          variant="ghost"
          size={triggerLabel ? "default" : "icon"}
          className={triggerClassName || "flex h-7 w-7 items-center justify-center rounded-md text-gray-400 dark:text-gray-500 transition-colors hover:text-gray-700 dark:hover:text-gray-300"}
          aria-label="Settings"
          aria-haspopup="dialog"
          onClick={openModal}
        >
          <Cog6ToothIcon className={triggerLabel ? "w-4 h-4" : "w-3.5 h-3.5"} />
          {triggerLabel && <span className="text-sm font-medium">{triggerLabel}</span>}
        </Button>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent
          className="!p-0 gap-0 w-[calc(100%-2rem)] sm:max-w-4xl h-[min(680px,90vh)] overflow-hidden shadow-2xl flex flex-col"
          showCloseButton
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">{t("settings")}</DialogTitle>

          <div className="flex flex-1 min-h-0 flex-col md:flex-row">
            {/* ── Sidebar ── */}
            <aside className="flex-none md:w-56 flex flex-col border-b md:border-b-0 md:border-r border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/50 md:max-h-none">
              {/* Sidebar header */}
              <div className="hidden md:flex items-center gap-2.5 px-5 py-[18px] h-[61px] border-b border-gray-100 dark:border-gray-800">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-200/80 dark:bg-gray-700">
                  <Cog6ToothIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{t("settings")}</span>
              </div>

              {/* Nav — horizontal scrolling on mobile, vertical on desktop */}
              <nav className="flex overflow-x-auto gap-1 pl-2 pr-12 md:pr-2 py-2 md:flex-col md:overflow-y-auto md:overflow-x-hidden md:py-2 modal-scrollbar">
                {/* Mobile: flat icon-only tabs */}
                <div className="flex gap-1 md:hidden">
                  {settingsSections.map((s) => {
                    const Icon = s.icon;
                    const isActive = s.id === activeSection;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setActiveSection(s.id)}
                        title={labelFor(s)}
                        className={cn(
                          "flex flex-col items-center gap-1 rounded-xl px-3 py-2 transition-colors shrink-0",
                          isActive
                            ? "bg-white dark:bg-gray-800 shadow-sm"
                            : "hover:bg-white/60 dark:hover:bg-gray-800/50",
                        )}
                      >
                        <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg", iconColors[s.id])}>
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <span className={cn("text-[9px] font-medium leading-none max-w-[48px] text-center truncate",
                          isActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400")}>
                          {labelFor(s).split(" ")[0]}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Desktop: grouped vertical nav */}
                <div className="hidden md:flex md:flex-col md:gap-0">
                  {groups.map((group, gi) => {
                    const secs = settingsSections.filter((s) => s.group === group.id);
                    return (
                      <div key={group.id} className={gi > 0 ? "mt-2" : ""}>
                        <div className="px-3 pb-1 pt-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                            {isRu ? group.labelRu : group.label}
                          </span>
                        </div>
                        {secs.map((s) => {
                          const Icon = s.icon;
                          const isActive = s.id === activeSection;
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => setActiveSection(s.id)}
                              aria-current={isActive ? "page" : undefined}
                              className={cn(
                                "mx-1 flex w-[calc(100%-0.5rem)] items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-all outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50",
                                isActive
                                  ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                                  : "text-gray-600 hover:bg-white/70 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/50 dark:hover:text-gray-200",
                              )}
                            >
                              <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-md", iconColors[s.id])}>
                                <Icon className="h-3.5 w-3.5" />
                              </span>
                              <span className={cn("font-medium text-sm", isActive && "font-semibold")}>{labelFor(s)}</span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </nav>

              {/* Logout — desktop only */}
              <div className="hidden md:block border-t border-gray-100 dark:border-gray-800 p-2 mt-auto shrink-0">
                <button
                  type="button"
                  onClick={() => { logout(); setShowModal(false); }}
                  className="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-red-500 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-red-100 text-red-500 dark:bg-red-900/40 dark:text-red-400">
                    <LogoutIcon className="h-3.5 w-3.5" />
                  </span>
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </aside>

            {/* ── Content ── */}
            <div className="flex flex-1 min-h-0 flex-col min-w-0">
              {/* Content header */}
              <div className="flex items-center gap-3 px-5 h-[61px] border-b border-gray-100 dark:border-gray-800 shrink-0">
                {ActiveIcon && (
                  <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg", iconColors[activeSection])}>
                    <ActiveIcon className="h-4 w-4" />
                  </span>
                )}
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {activeData ? labelFor(activeData) : t("settings")}
                </h2>
              </div>

              {/* Scrollable content */}
              <div className="settings-pane flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 modal-scrollbar">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={activeSection}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.14, ease: "easeOut" }}
                  >
                    <ActiveComponent />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
